import React, { useState, useEffect, useCallback } from 'react';

export function GalleryLightbox({ images = [], initialIndex = 0, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStartX, setTouchStartX] = useState(null);

  const handleNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  }, [images.length]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, handleNext, handlePrev]);

  if (!images.length) return null;

  const currentImage = images[currentIndex];

  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diffX = touchStartX - touchEndX;

    if (diffX > 50) handleNext();
    else if (diffX < -50) handlePrev();

    setTouchStartX(null);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 animate-in fade-in duration-200"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-5 right-5 p-3 rounded-full bg-slate-900/80 text-slate-300 hover:text-white hover:bg-slate-800 transition-colors z-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
        aria-label="Luk galleri"
      >
        <i className="fa-solid fa-xmark text-xl" aria-hidden="true" />
      </button>

      {/* Counter */}
      <div className="absolute top-5 left-5 text-slate-400 text-xs font-semibold tracking-widest bg-slate-900/80 px-4 py-2 rounded-full border border-slate-800">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={handlePrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-4 rounded-full bg-slate-900/80 text-slate-300 hover:text-white hover:bg-slate-800 transition-all z-40 focus:outline-none focus:ring-2 focus:ring-amber-500 hidden sm:flex items-center justify-center"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left text-xl" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-4 rounded-full bg-slate-900/80 text-slate-300 hover:text-white hover:bg-slate-800 transition-all z-40 focus:outline-none focus:ring-2 focus:ring-amber-500 hidden sm:flex items-center justify-center"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right text-xl" aria-hidden="true" />
          </button>
        </>
      )}

      {/* Main Image */}
      <div className="max-w-5xl max-h-[85vh] flex flex-col items-center justify-center p-2">
        <img
          src={typeof currentImage === 'string' ? currentImage : currentImage.src || currentImage.url}
          alt={typeof currentImage === 'object' ? currentImage.alt || 'Galleribillede' : 'Galleribillede'}
          className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl select-none"
        />
        {(typeof currentImage === 'object' && currentImage.alt) && (
          <p className="mt-4 text-xs text-slate-300 text-center max-w-xl">
            {currentImage.alt}
          </p>
        )}
      </div>
    </div>
  );
}
